import React, { useState, useRef } from 'react';
import { uploadScope } from '../api';
import { FileUp, FileCheck, X, FileText, Loader } from './Icons';
import './UploadModal.css';

export default function UploadModal({ projectId, projectName, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef();

  function handleFile(files) {
    const f = files?.[0];
    if (f && f.type === 'application/pdf') {
      setFile(f);
      setError(null);
    } else if (f) {
      setError('Only PDF files are supported.');
    }
  }

  function handleRemoveFile(e) {
    e.stopPropagation();
    setFile(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', projectId);
      await uploadScope(formData);
      // Backend returns 202 immediately — AI runs in background.
      // Close the modal and let ProjectDetail poll for status.
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload PDF. Please try again.');
      setUploading(false);
    }
  }

  const fileSizeMB = file ? (file.size / 1024 / 1024).toFixed(2) : null;

  return (
    <div className="modal-overlay animate-fade-in" onClick={uploading ? undefined : onClose}>
      <div className="upload-modal-panel" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="upload-modal-header">
          <div className="upload-modal-header-left">
            <div className="upload-modal-icon-wrap">
              <FileUp size={18} />
            </div>
            <div>
              <h2 className="upload-modal-title">Upload Scope Document</h2>
              <p className="upload-modal-sub">
                {projectName && <><span className="upload-project-tag">{projectName}</span></>}
              </p>
            </div>
          </div>
          {!uploading && (
            <button className="upload-close-btn" onClick={onClose} aria-label="Close">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="upload-modal-body">
          {!uploading ? (
            <>
              {/* Drop Zone */}
              <div
                className={`upload-dropzone ${dragActive ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
                onDragEnter={e => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); setDragActive(false); handleFile(e.dataTransfer.files); }}
                onClick={() => !file && inputRef.current?.click()}
                role="button"
                tabIndex={0}
              >
                <input ref={inputRef} type="file" accept=".pdf" hidden onChange={e => handleFile(e.target.files)} />

                {file ? (
                  <div className="upload-file-preview">
                    <div className="upload-file-icon-wrap">
                      <FileCheck size={28} />
                    </div>
                    <div className="upload-file-info">
                      <div className="upload-file-name">{file.name}</div>
                      <div className="upload-file-meta">{fileSizeMB} MB · PDF</div>
                    </div>
                    <button className="upload-file-remove" onClick={handleRemoveFile} title="Remove file">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="upload-dropzone-empty">
                    <div className="upload-dropzone-icon">
                      <FileText size={32} />
                    </div>
                    <div className="upload-dropzone-text">
                      <strong>Drop your SOW contract PDF here</strong>
                      <span>or <u>click to browse</u></span>
                    </div>
                    <div className="upload-dropzone-badge">PDF only · Max 10 MB</div>
                  </div>
                )}
              </div>

              {/* What we extract */}
              {!file && (
                <div className="upload-extracts">
                  <div className="upload-extracts-label">What we extract</div>
                  <div className="upload-extracts-chips">
                    {['Deliverables', 'Out of scope', 'Timelines', 'Agreed additions'].map(t => (
                      <span key={t} className="upload-extract-chip">{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div className="upload-error-box">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  {error}
                </div>
              )}
            </>
          ) : (
            /* Uploading state — brief while PDF is being sent to server */
            <div className="upload-processing">
              <div className="upload-simple-spinner">
                <Loader size={36} className="spin" />
              </div>
              <p className="upload-processing-title">Uploading document…</p>
              <p className="upload-processing-note">
                You'll be taken back to your project in just a moment.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {!uploading && (
          <div className="upload-modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-primary btn-lg"
              onClick={handleUpload}
              disabled={!file}
            >
              <FileUp size={16} />
              Process Document
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
